import React, { useState, useEffect, useCallback } from "react";
import { Loader, Pencil, AlertTriangle, Layers } from "lucide-react";
import SearchableSelect from "../SearchableSelect";
import * as inventoryService from "../../api/inventoryService";
import { useNotification } from "../../contexts/NotificationContext";

// ── Set Stok Modal ─────────────────────────────────────────────────────────────
function SetAddonStockModal({ isOpen, onClose, addon, onSuccess }) {
  const { showNotification } = useNotification();
  const [newStock, setNewStock] = useState("");
  const [minStock, setMinStock] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && addon) {
      setNewStock(addon.current_stock?.toString() || "0");
      setMinStock(addon.minimum_stock?.toString() || "0");
      setNotes("");
    }
  }, [isOpen, addon]);

  if (!isOpen || !addon) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseInt(newStock, 10);
    const minVal = parseInt(minStock, 10);
    if (isNaN(val) || val < 0) {
      showNotification("error", "Stok tidak valid");
      return;
    }
    if (isNaN(minVal) || minVal < 0) {
      showNotification("error", "Stok minimum tidak valid");
      return;
    }
    try {
      setSaving(true);
      await inventoryService.setAddonStock(addon.addon_id, val, minVal, notes);
      showNotification("success", `Stok ${addon.addon_name} diperbarui`);
      onSuccess();
      onClose();
    } catch (err) {
      showNotification("error", err?.message || "Gagal memperbarui stok");
    } finally {
      setSaving(false);
    }
  };

  const diff = parseInt(newStock, 10) - (addon.current_stock ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Set Stok Ekstra</h2>
            <p className="text-sm text-slate-500 mt-0.5">{addon.addon_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form id="set-addon-stock-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Current stock info */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-5 py-4">
              <span className="text-sm font-medium text-slate-600">Stok Saat Ini</span>
              <span className="text-lg font-bold text-slate-900">{addon.current_stock ?? 0}</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* New stock input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Stok Baru <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    value={newStock}
                    onChange={(e) => setNewStock(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                    required
                    autoFocus
                  />
                  {!isNaN(diff) && diff !== 0 && (
                    <div className={`absolute -bottom-6 left-0 text-xs font-medium ${diff > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {diff > 0 ? `+${diff} akan ditambahkan` : `${diff} akan dikurangi`}
                    </div>
                  )}
                </div>
              </div>

              {/* Minimum stock input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Stok Minimum
                </label>
                <input
                  type="number"
                  min={0}
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Catatan Penyesuaian
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Contoh: restok dari supplier atau barang rusak"
                rows={2}
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all resize-none"
              />
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-200/50 transition-colors"
          >
            Batal
          </button>
          <button
            type="submit"
            form="set-addon-stock-form"
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Table Component ────────────────────────────────────────────────────────
export default function AddonStockTable({ searchTerm = "" }) {
  const { showNotification } = useNotification();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryService.getAddonStocks({
        page,
        limit: itemsPerPage,
        query: searchTerm,
      });
      setData(res?.data || []);
      setMeta(res?.meta || { page, limit: itemsPerPage, total: 0 });
    } catch (err) {
      const msg = err?.message || "Gagal memuat stok ekstra";
      setError(msg);
      showNotification("error", msg);
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, searchTerm, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleOpenModal = (addon) => {
    setSelectedAddon(addon);
    setIsModalOpen(true);
  };

  const handleSuccess = () => {
    setRefreshKey((k) => k + 1);
  };

  const totalEntries = meta.total || 0;
  const maxPages = Math.max(1, Math.ceil(totalEntries / itemsPerPage));
  const startEntry = totalEntries === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const endEntry = Math.min(page * itemsPerPage, totalEntries);

  return (
    <div className="bg-white rounded-lg border border-gray-200 mt-6">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-20">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Nama Ekstra
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                    Harga Jual
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Stock Saat Ini
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Minimum Stock
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? "Tidak ada ekstra yang cocok" : "Belum ada data stok ekstra"}
                    </td>
                  </tr>
                ) : (
                  data.map((item) => {
                    const isLow = item.current_stock <= item.minimum_stock && item.minimum_stock > 0;
                    const isEmpty = item.current_stock === 0;

                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleOpenModal(item)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors flex items-center justify-center"
                            title="Set Stok Ekstra"
                          >
                            <Pencil className="w-4 h-4 text-slate-600" />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-slate-800">
                          <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-teal-600 shrink-0" />
                            <span>{item.addon_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-right">
                          {item.sell_price > 0
                            ? `Rp ${item.sell_price.toLocaleString("id-ID")}`
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`text-sm font-bold ${isEmpty ? "text-red-600" : isLow ? "text-orange-600" : "text-slate-900"}`}>
                            {item.current_stock}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">
                          {item.minimum_stock > 0 ? item.minimum_stock : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {isEmpty ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                              <AlertTriangle className="w-3 h-3" /> Habis
                            </span>
                          ) : isLow ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              <AlertTriangle className="w-3 h-3" /> Menipis
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Tersedia
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Standard Table Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {startEntry} to {endEntry} of {totalEntries} entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => {
                  setItemsPerPage(Number(value));
                  setPage(1);
                }}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                ‹‹
              </button>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = page;
                if (page === 1) p = 1;
                else if (page === maxPages) p = maxPages - 2;
                else p = page - 1;
                if (maxPages < 3) p = 1;
                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      page === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setPage(Math.min(maxPages, page + 1))}
                disabled={page === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                ›
              </button>
              <button
                onClick={() => setPage(maxPages)}
                disabled={page === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                ››
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      <SetAddonStockModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        addon={selectedAddon}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
